import saleinvoiceService from '../../../services/saleinvoiceService';
import filterService from '../../../services/filterService';

import {
    setSaleInvoiceLoading,
    setSaleInvoiceError,
    setSaleInvoiceList,
    setSaleInvoiceCurrent,
    setSaleInvoicePagination,
    setSaleInvoiceSort,
    setSaleInvoiceFilters,
    setSaleInvoiceDefaultFilter,
    setSaleInvoiceSettings,
    clearSaleInvoiceCurrent,
    clearSaleInvoiceError,
    setSaleInvoiceFilterLoading,
    changeSaleInvoiceFilterCondition,
    addSaleInvoiceFilterCondition,
    updateSaleInvoiceFilterConditionRule,
    updateSaleInvoiceFilterConditionValue,
    removeSaleInvoiceFilterCondition,
    setSaleInvoiceSelected,
    resetSaleInvoiceFiltersAndPagination
} from './saleinvoiceSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().saleinvoice.isSettingsInitialized) {
            dispatch(setSaleInvoiceLoading(true));
            const response = await saleinvoiceService.getSettings();

            // Set the settings
            dispatch(setSaleInvoiceSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setSaleInvoiceDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setSaleInvoiceError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setSaleInvoiceLoading(false));
    }
};

export const fetchSaleinvoices = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().saleinvoice;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setSaleInvoiceLoading(true));
        const response = await saleinvoiceService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setSaleInvoiceList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchSaleinvoices:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setSaleInvoiceError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setSaleInvoiceLoading(false));
    }
};

export const fetchSaleInvoice = (id) => async (dispatch) => {
    try {
        dispatch(setSaleInvoiceLoading(true));
        const response = await saleinvoiceService.getOne(id);
        dispatch(setSaleInvoiceCurrent(response.data));
    } catch (error) {
        dispatch(setSaleInvoiceError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setSaleInvoiceLoading(false));
    }
};

export const createSaleInvoice = (data) => async (dispatch) => {
    try {
        const response = await saleinvoiceService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateSaleInvoice = (id, data) => async (dispatch) => {
    try {
        const response = await saleinvoiceService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteSaleInvoice = (id) => async (dispatch) => {
    try {
        dispatch(setSaleInvoiceLoading(true));
        const response = await saleinvoiceService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setSaleInvoiceError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setSaleInvoiceLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setSaleInvoicePagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setSaleInvoiceSort(sort));
};

export const searchSaleinvoices = (query) => (dispatch, getState) => {
    const currentFilters = getState().saleinvoice.filters;
    dispatch(setSaleInvoiceFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setSaleInvoiceDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeSaleInvoiceFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addSaleInvoiceFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateSaleInvoiceFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateSaleInvoiceFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeSaleInvoiceFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setSaleInvoiceFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().saleinvoice.settings;
            dispatch(setSaleInvoiceSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setSaleInvoiceFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setSaleInvoiceFilterLoading(false));
        dispatch(setSaleInvoiceError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setSaleInvoiceSelected(selectedRows));
    };
};

export const clearCurrentSaleInvoice = () => {
    return (dispatch) => {
        dispatch(clearSaleInvoiceCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetSaleInvoiceFiltersAndPagination());
};

export const updateSaleInvoiceCustomerLpo = (id, customerLpo) => async (dispatch) => {
    try {
        const response = await saleinvoiceService.updateCustomerLpo(id, customerLpo);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};